import Utils      from "@/core/utils/Utils";
import DateSimple from '@/core/types/DateSimple';
import Core       from '@/core/Core';
import TimeStamp  from '@/core/types/TimeStamp';
import SupplyOfferTariff from "@/domains/SupplyOffers/SupplyOfferTariff";
import SupplyOffer from "@/domains/SupplyOffers/SupplyOffer";

/** Аргумент любого парсера — пара значений до/после изменения. */
export type Diff<T = unknown> = {
  oldValue: T | null | undefined;
  newValue: T | null | undefined;
};

type IntRangeValue   = { start: number; end: number };
type DateRangeValue  = { start: string; end: string };
type ObjectRefValue  = { id?: number | string; name?: string; stringRepresentation?: string };
type PriceValue      = { value?: number; currencyCode?: string; [code: string]: unknown };
type PaymentTermValue = { stringRepresentation: string };
type SupplyOfferValue = {
  sellerCompany: { name: string };
  manufacturer:  { name: string };
};
type TenderBidValue   = { priceFullBuyerWithDeferredPayment: PriceValue };
type CoordinatesValue = { latitude: number; longitude: number };

// todo удалить, он кажется нафиг нам стал не нужен
const tenderPriceFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const priceFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const sumFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Парсеры диффов changelog в человекочитаемые строки.
 *
 * Чистые статические функции без состояния — собраны в класс по канону ядра.
 * Диспетчеризация по типу поля — в ChangelogsService.resolveChangeRecords.
 */
class ChangelogFormatters {

  static parseObjectDiff({ oldValue, newValue }: Diff<ObjectRefValue>): string {
    let resultMessage = '';

    // address.stringRepresentation был добавлен для фида склада; там id не прилетает,
    //а при неявном создании склада - name тоже равен null
    if (oldValue?.id || oldValue?.stringRepresentation) {
      const displayName = oldValue.name ?? oldValue?.stringRepresentation ?? oldValue.address?.stringRepresentation;
      if (displayName) {
        resultMessage += ` с ${displayName} `;
      }
    }

    if (newValue?.id || oldValue?.stringRepresentation) {
      const displayName = newValue?.name ?? newValue?.stringRepresentation ?? newValue?.address?.stringRepresentation;
      if (displayName) {
        resultMessage += ` <b>на</b> ${displayName} `;
      }
    }

    return resultMessage;
  }

  static parseIntRangeDiff({ oldValue, newValue }: Diff<IntRangeValue>): string {
    let resultMessage = '';

    if (Utils.number.isNumber(oldValue?.start) && Utils.number.isNumber(oldValue?.end)) {
      resultMessage += ` с [${oldValue!.start}, ${oldValue!.end}] `;
    }

    if (Utils.number.isNumber(newValue?.start) && Utils.number.isNumber(newValue?.end)) {
      resultMessage += ` на [${newValue!.start}, ${newValue!.end}] `;
    } else {
      resultMessage += ' без ограничений ';
    }

    return resultMessage;
  }

  static parseDateRangeDiff({ oldValue, newValue }: Diff<DateRangeValue>): string {
    let resultMessage = '';

    if (oldValue?.start && oldValue?.end) {
      resultMessage += ` с [${DateSimple.fromServer(oldValue.start)}, ${DateSimple.fromServer(oldValue.end)}] `;
    }

    if (newValue?.start && newValue?.end) {
      resultMessage += ` на [${DateSimple.fromServer(newValue.start)}, ${DateSimple.fromServer(newValue.end)}] `;
    }

    return resultMessage;
  }

  static parsePrimitiveDiff({ oldValue, newValue }: Diff<string | number>): string {
    let resultMessage = '';

    if (['number', 'string'].includes(typeof oldValue)) {
      resultMessage += ` с "${oldValue}"`;
    }

    if (['number', 'string'].includes(typeof newValue)) {
      resultMessage += ` <b>на</b> "${newValue}"`;
    }

    return resultMessage;
  }

  /** По сути повтор parsePrimitiveDiff но со знаком %,
   * пока не знаю как было бы просто в одну функцию затолкать, без особых усилий */
  static parsePrimitiveDiffWithPercentSign({ oldValue, newValue }: Diff<string | number>): string {
    let resultMessage = '';

    if (['number', 'string'].includes(typeof oldValue)) {
      resultMessage += ` с ${oldValue}%`;
    }

    if (['number', 'string'].includes(typeof newValue)) {
      resultMessage += ` <b>на</b> ${newValue}%`;
    }

    return resultMessage;
  }

  static parseDateDiff({ oldValue, newValue }: Diff<string>): string {
    let resultMessage = '';

    if (oldValue) {
      resultMessage += ` с ${DateSimple.fromServer(oldValue)}`;
    }

    if (newValue) {
      resultMessage += ` на ${DateSimple.fromServer(newValue)}`;
    }

    return resultMessage;
  }

  static parseDateTimeDiff({ oldValue, newValue }: Diff<string>): string {
    let resultMessage = '';

    if (oldValue) {
      resultMessage += ` с ${TimeStamp.fromServer(oldValue)}`;
    }

    if (newValue) {
      resultMessage += ` на ${TimeStamp.fromServer(newValue)}`;
    }

    return resultMessage;
  }

  static parseTenderBid({ oldValue, newValue }: Diff<TenderBidValue>): string {
    return ChangelogFormatters.parseTenderPriceDiff({
      oldValue: oldValue?.priceFullBuyerWithDeferredPayment,
      newValue: newValue?.priceFullBuyerWithDeferredPayment,
    });
  }

  static parsePriceDiff({ oldValue, newValue }: Diff<PriceValue>): string {
    let resultMessage = '';

    if (typeof oldValue === 'object' && oldValue !== null && !Array.isArray(oldValue)) {
      const currencyCode = oldValue[Core.displayedCurrencyCode]
        ? Core.displayedCurrencyCode
        : oldValue.currencyCode;
      const symbol = Core.utils.money.getSymbolByCurrency(currencyCode);
      const rawValue = (oldValue[currencyCode as string] as PriceValue)?.value ?? oldValue.value;

      if (rawValue != null) {
        resultMessage += ` c ${priceFormatter.format(rawValue)} ${symbol}`;
      } else {
        resultMessage += ' c --';
      }
    }

    if (typeof newValue === 'object' && newValue !== null && !Array.isArray(newValue)) {
      const currencyCode = newValue[Core.displayedCurrencyCode]
        ? Core.displayedCurrencyCode
        : newValue.currencyCode;
      const symbol = Core.utils.money.getSymbolByCurrency(currencyCode);
      const rawValue = (newValue[currencyCode as string] as PriceValue)?.value ?? newValue.value;

      if (rawValue != null) {
        resultMessage += ` на ${priceFormatter.format(rawValue)} ${symbol}`;
      } else {
        resultMessage += ' на --';
      }
    }

    return resultMessage;
  }

  static parseTenderPriceDiff({ oldValue, newValue }: Diff<PriceValue | number>): string {
    let resultMessage = '';

    if (typeof oldValue === 'object' && oldValue !== null && !Array.isArray(oldValue)) {
      const currencyCode = oldValue[Core.displayedCurrencyCode]
        ? Core.displayedCurrencyCode
        : Object.keys(oldValue)[0];
      const symbol = Core.utils.money.getSymbolByCurrency(currencyCode);
      try {
        resultMessage += ` c ${tenderPriceFormatter.format((oldValue[currencyCode] as PriceValue).value!)} ${symbol}`;
      } catch (err) {
        resultMessage += ' c --';
      }
    } else if (oldValue) {
      resultMessage += ` с ${tenderPriceFormatter.format(oldValue as number)}`;
    }

    if (typeof newValue === 'object' && newValue !== null && !Array.isArray(newValue)) {
      const currencyCode = newValue[Core.displayedCurrencyCode]
        ? Core.displayedCurrencyCode
        : Object.keys(newValue)[0];
      const symbol = Core.utils.money.getSymbolByCurrency(currencyCode);
      try {
        resultMessage += ` на ${tenderPriceFormatter.format((newValue[currencyCode] as PriceValue).value!)} ${symbol}`;
      } catch (err) {
        resultMessage += ' на --';
      }
    } else if (newValue) {
      resultMessage += ` на ${tenderPriceFormatter.format(newValue as number)}`;
    }

    return resultMessage;
  }

  static parseSumDiff({ oldValue, newValue }: Diff<number>): string {
    let resultMessage = '';

    if (oldValue) {
      resultMessage += ` с ${sumFormatter.format(oldValue)}`;
    }

    if (newValue) {
      resultMessage += ` на ${sumFormatter.format(newValue)}`;
    }

    return resultMessage;
  }

  /**
   * Условия оплаты. Значение приходит и одной частью платежа, и списком:
   * в реквизитах заказа хранится paymentTerms, а у ставки и предзаказа
   * часть одна. Перегрузки нужны, чтобы вызывающий не приводил тип руками.
   */
  static parsePaymentTerm(diff: Diff<PaymentTermValue>): string;
  static parsePaymentTerm(diff: Diff<PaymentTermValue[]>): string;
  static parsePaymentTerm({ oldValue, newValue }: Diff<PaymentTermValue | PaymentTermValue[]>): string {
    let resultMessage = '';

    const oldFormatted = ChangelogFormatters.formatPaymentTermValue(oldValue);
    const newFormatted = ChangelogFormatters.formatPaymentTermValue(newValue);

    /** Квадратные скобки — граница значения: части платежа склеиваются
     *  запятой, и без них не видно, где кончается старое и начинается новое. */
    if (oldFormatted !== null) {
      resultMessage += ` с [${oldFormatted}]`;
    }

    if (newFormatted !== null) {
      resultMessage += ` на [${newFormatted}]`;
    }

    return resultMessage;
  }

  /**
   * Часть платежа или их список к строке. Пустой список — null, как и
   * отсутствие значения: иначе в ленте появилось бы « с » без содержания.
   */
  static formatPaymentTermValue(value: PaymentTermValue | PaymentTermValue[] | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    else if (Array.isArray(value)) {
      if (value.length === 0) {
        return null;
      } else {
        return value.map(item => item.stringRepresentation).join(', ');
      }
    }
    else {
      return value.stringRepresentation;
    }
  }

  static parseSupplyOffer({ oldValue, newValue }: Diff<SupplyOfferValue>): string {
    let resultMessage = '';

    if (oldValue?.manufacturer) {
      resultMessage += ` с поставщика [${oldValue.sellerCompany.name}-${oldValue.manufacturer.name}]`;
    }

    if (newValue) {
      resultMessage += ` поставщику [${newValue.sellerCompany.name}-${newValue.manufacturer.name}]`;
    }

    return resultMessage;
  }

  static parseManufacturers({ oldValue, newValue }: Diff<string>): string {
    const oldIds = oldValue ? oldValue.split(',') : [];
    const newIds = newValue ? newValue.split(',') : [];

    const removed = oldIds.filter(id => !newIds.includes(id));
    const added   = newIds.filter(id => !oldIds.includes(id));

    let result = '';

    if (!oldValue && added.length) {
      result += `${added.length > 1
        ? 'Установлены <b>производители:</b>'
        : 'Установлен <b>производитель:</b>'} ${added.join(', ')}.`;
    } else {
      if (added.length) {
        result += `${added.length > 1
          ? 'Добавлены <b>производители:</b>'
          : 'Добавлен <b>производитель:</b>'} ${added.join(', ')}. `;
      }
      if (removed.length) {
        result += `${removed.length > 1
          ? 'Удалены <b>производители:</b>'
          : 'Удалён <b>производитель:</b>'} ${removed.join(', ')}.`;
      }
    }

    return result.trim();
  }

  static parseCommentDiff({ oldValue, newValue }: Diff<string>): string {
    let resultMessage = '';

    if (oldValue && newValue?.length === 0) {
      resultMessage += `Удален <b>комментарий</b> "${oldValue}"`;
    }

    if (!oldValue && newValue && newValue.length > 0) {
      resultMessage += `Добавлен <b>комментарий</b> "${newValue}"`;
    }

    if (oldValue && oldValue.length > 0 && newValue && newValue.length > 0) {
      resultMessage += `Изменен <b>комментарий</b> с "${oldValue}" на "${newValue}"`;
    }

    return resultMessage;
  }

  static parseCoordinates({ oldValue, newValue }: Diff<CoordinatesValue>): string {
    let resultMessage = '';

    if (!oldValue && newValue) {
      // Добавлено
      resultMessage += `<br>широта: ${newValue.latitude} долгота: ${newValue.longitude}`;
    } else if (oldValue && newValue) {
      // Обновлено
      resultMessage += `<br>с широта: ${oldValue.latitude} долгота: ${oldValue.longitude}<br>на широта: ${newValue.latitude} долгота: ${newValue.longitude}`;
    } else if (oldValue && !newValue) {
      // Удалено
      resultMessage += `<br>широта: ${oldValue.latitude} долгота: ${oldValue.longitude}`;
    }

    return resultMessage;
  }

  static parseCustomBoolean({ newValue }: Diff<boolean>, descriptorTitle: string): string {
    const action = newValue === false ? 'удалена' : 'установлена';
    return `Отметка <b>${descriptorTitle}</b> ${action}`;
  }

  static parseTenderParticipation({ oldValue, newValue }: Diff<boolean>): string {
    const action = newValue === false ? 'удалена' : 'установлена';
    return `Отметка <b>Участие</b> ${action}`;
  }

  /**
   * Значение характеристики к строке. Значение приходит в одном из видов:
   * массив выбранных вариантов (selectableSingle/Multi), число (numeric),
   * строка (string/link). Пустое значение — null, его разбирает вызывающий:
   * от него зависит выбор префикса Установлено/Удалено.
   */
  static formatCharacteristicValue(value: unknown): string | null {
    let result: string | null;

    if (value === null || value === undefined || value === '') {
      result = null;
    }
    else if (Array.isArray(value)) {
      if (value.length === 0) {
        result = null;
      }
      else {
        result = value
          .map(item => ChangelogFormatters.formatCharacteristicValue(item))
          .filter(item => item !== null)
          .join(', ');
      }
    }
    else if (typeof value === 'object') {
      result = (value as ObjectRefValue).name ?? null;
    }
    else {
      result = String(value);
    }

    return result;
  }

  /**
   * Дифф значения характеристики. Название рамки приходит в самом ключе
   * (characteristics[<название>]) — дескриптора у такого поля нет, поэтому
   * префикс собирается здесь, а не в resolveChangeRecords.
   */
  static parseCharacteristicDiff(name: string, { oldValue, newValue }: Diff): string {
    const oldFormatted = ChangelogFormatters.formatCharacteristicValue(oldValue);
    const newFormatted = ChangelogFormatters.formatCharacteristicValue(newValue);

    let resultMessage: string;

    if (oldFormatted === null && newFormatted !== null) {
      resultMessage = `Установлено <b>Характеристика [${name}]</b> на "${newFormatted}"`;
    }
    else if (oldFormatted !== null && newFormatted === null) {
      resultMessage = `Удалено <b>Характеристика [${name}]</b> со значения "${oldFormatted}"`;
    }
    else if (oldFormatted === null && newFormatted === null) {
      resultMessage = `Изменено <b>Характеристика [${name}]</b>`;
    }
    else {
      resultMessage = `Изменено <b>Характеристика [${name}]</b> с "${oldFormatted}" <b>на</b> "${newFormatted}"`;
    }

    return resultMessage;
  }

  /**
   * Разбор изменения тарифной сетки: на входе два состояния целиком,
   * на выходе — готовое сообщение, по абзацу на каждый добавленный,
   * удалённый и изменённый тариф.
   */
  static parseTariffDiff({ oldValue, newValue }: Diff<any[]>): string {
    const messages: string[] = [];

    // обе стороны пусты — правка тарифов не затрагивала
    if (oldValue === null && newValue === null) {
      return '';
    }

    // дальше обе стороны обходятся одинаково, поэтому отсутствующую
    // заменяем пустым массивом — иначе на каждом шаге пришлось бы
    // проверять её на null
    let oldArr: any[];
    let newArr: any[];

    if (oldValue === null || oldValue === undefined) {
      oldArr = [];
    }
    else {
      oldArr = oldValue;
    }

    if (newValue === null || newValue === undefined) {
      newArr = [];
    }
    else {
      newArr = newValue;
    }

    // тарифы в массивах лежат в произвольном порядке, сопоставлять их
    // попарно по индексу нельзя — единственная общая точка это id,
    // поэтому обе стороны переводим в мапы по id
    const oldMap = new Map<number, SupplyOfferTariff>();
    const newMap = new Map<number, SupplyOfferTariff>();

    oldArr.forEach(item => {
      oldMap.set(item.id, SupplyOfferTariff.fromServer(item));
    });

    newArr.forEach(item => {
      newMap.set(item.id, SupplyOfferTariff.fromServer(item));
    });

    // объединение ключей: id только в старой мапе — удаление,
    // только в новой — добавление, в обеих — кандидат на изменение
    const allIds = new Set<number>();

    oldMap.forEach((_, id) => {
      allIds.add(id);
    });

    newMap.forEach((_, id) => {
      allIds.add(id);
    });

    allIds.forEach(id => {
      let oldTariff: SupplyOfferTariff | null;
      let newTariff: SupplyOfferTariff | null;

      if (oldMap.has(id) === true) {
        oldTariff = oldMap.get(id)!;
      }
      else {
        oldTariff = null;
      }

      if (newMap.has(id) === true) {
        newTariff = newMap.get(id)!;
      }
      else {
        newTariff = null;
      }

      if (oldTariff === null && newTariff !== null) {
        messages.push(`Установлено <b>тариф</b> со значением "${ChangelogFormatters.formatTariffValue(newTariff)}"`);
      }
      else if (oldTariff !== null && newTariff === null) {
        messages.push(`Удалено <b>тариф</b> со значением "${ChangelogFormatters.formatTariffValue(oldTariff)}"`);
      }
      else if (oldTariff !== null && newTariff !== null) {
        // тариф есть с обеих сторон — сравниваем по qty, price и actualFor.
        // version и isDeleted в новом значении всегда приходят пустыми,
        // это шум сериализации, а не изменение
        if (oldTariff.isSameTariff(newTariff) === false) {
          messages.push(`Изменено <b>тариф</b> с "${ChangelogFormatters.formatTariffValue(oldTariff)}" <b>на</b> "${ChangelogFormatters.formatTariffValue(newTariff)}"`);
        }
        else {
          // тариф не изменился — сообщения нет
        }
      }
      else {
        // обеих сторон нет — недостижимо, id взят из объединения ключей
      }
    });

    return messages.join('<br><br>');
  }

  static formatTariffValue(tariff: SupplyOfferTariff): string {
    const qty = tariff.qty.toString({middle: tariff.qty._end ? 'до' : ''});
    const price = tariff.price.toString()
    const period = tariff.actualFor.toString({middle: tariff.actualFor._end ? 'по' : ''});

    return `диапазон количества: ${qty}, цена: ${price}, период действия цены: ${period}`;
  }
}

export default ChangelogFormatters;
